"use client";

import {useCallback, useEffect, useRef, useState} from "react";

declare global {
  interface Window {
    FB?: {
      init: (params: {
        appId: string;
        autoLogAppEvents: boolean;
        xfbml: boolean;
        version: string;
      }) => void;
      login: (
        callback: (response: FBLoginResponse) => void,
        options: {
          config_id: string;
          response_type: string;
          override_default_response_type: boolean;
          extras: {setup: Record<string, unknown>};
        }
      ) => void;
    };
    fbAsyncInit?: () => void;
  }
}

interface FBLoginResponse {
  authResponse?: {
    code: string;
    accessToken?: string;
    userID?: string;
  };
  status: string;
}

interface EmbeddedSignupData {
  phone_number_id: string;
  waba_id: string;
  business_id?: string;
}

interface EmbeddedSignupEvent {
  type: string;
  event: "FINISH" | "CANCEL" | "ERROR";
  data: EmbeddedSignupData | {current_step?: string};
}

export interface WhatsAppSignupResult {
  code: string;
  wabaId: string;
  phoneNumberId: string;
  businessId?: string;
}

interface UseWhatsAppEmbeddedSignupReturn {
  launchSignup: () => void;
  isSDKReady: boolean;
  isLoading: boolean;
  error: string | null;
}

export function useWhatsAppEmbeddedSignup(
  onSuccess: (result: WhatsAppSignupResult) => void,
  onCancel?: () => void
): UseWhatsAppEmbeddedSignupReturn {
  const [isSDKReady, setIsSDKReady] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const signupDataRef = useRef<EmbeddedSignupData | null>(null);
  const pendingLaunchRef = useRef(false);
  const onSuccessRef = useRef(onSuccess);
  const onCancelRef = useRef(onCancel);
  onSuccessRef.current = onSuccess;
  onCancelRef.current = onCancel;

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      try {
        const originHost = new URL(event.origin).hostname;
        if (
          originHost !== "facebook.com" &&
          !originHost.endsWith(".facebook.com")
        )
          return;
      } catch {
        return;
      }

      try {
        const data: EmbeddedSignupEvent =
          typeof event.data === "string" ? JSON.parse(event.data) : event.data;

        if (data.type === "WA_EMBEDDED_SIGNUP") {
          if (data.event === "FINISH") {
            signupDataRef.current = data.data as EmbeddedSignupData;
          } else if (data.event === "CANCEL") {
            setIsLoading(false);
            onCancelRef.current?.();
          } else if (data.event === "ERROR") {
            setIsLoading(false);
            setError("An error occurred during WhatsApp signup");
          }
        }
      } catch {
        // non-JSON message from other sources
      }
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, []);

  useEffect(() => {
    if (window.FB) {
      setIsSDKReady(true);
    }
  }, []);

  const openFBLogin = useCallback(() => {
    if (!window.FB) return;

    signupDataRef.current = null;

    const fbLoginCallback = (response: FBLoginResponse) => {
      if (response.authResponse?.code) {
        const signupData = signupDataRef.current;
        if (signupData) {
          onSuccessRef.current({
            code: response.authResponse.code,
            wabaId: signupData.waba_id,
            phoneNumberId: signupData.phone_number_id,
            businessId: signupData.business_id,
          });
        } else {
          setError("Signup completed but asset data was not received");
        }
      } else {
        onCancelRef.current?.();
      }
      setIsLoading(false);
    };

    const configId = process.env.NEXT_PUBLIC_FB_CONFIG_ID;
    if (!configId) {
      setError("NEXT_PUBLIC_FB_CONFIG_ID is not configured");
      setIsLoading(false);
      return;
    }

    window.FB.login(fbLoginCallback, {
      config_id: configId,
      response_type: "code",
      override_default_response_type: true,
      extras: {setup: {}},
    });
  }, []);

  const loadSDK = useCallback(() => {
    const appId = process.env.NEXT_PUBLIC_FB_APP_ID;
    if (!appId) {
      setError("NEXT_PUBLIC_FB_APP_ID is not configured");
      setIsLoading(false);
      return;
    }

    window.fbAsyncInit = () => {
      window.FB!.init({
        appId,
        autoLogAppEvents: true,
        xfbml: true,
        version: process.env.NEXT_PUBLIC_GRAPH_API_VERSION || "v25.0",
      });
      setIsSDKReady(true);

      if (pendingLaunchRef.current) {
        pendingLaunchRef.current = false;
        openFBLogin();
      }
    };

    if (!document.getElementById("facebook-jssdk")) {
      const script = document.createElement("script");
      script.id = "facebook-jssdk";
      script.src = "https://connect.facebook.net/en_US/sdk.js";
      script.async = true;
      script.defer = true;
      script.onerror = () => {
        setError("Failed to load Facebook SDK");
        setIsLoading(false);
        pendingLaunchRef.current = false;
      };
      document.body.appendChild(script);
    }
  }, [openFBLogin]);

  const launchSignup = useCallback(() => {
    setError(null);
    setIsLoading(true);

    if (window.FB) {
      openFBLogin();
      return;
    }

    pendingLaunchRef.current = true;
    loadSDK();
  }, [openFBLogin, loadSDK]);

  return {launchSignup, isSDKReady, isLoading, error};
}
